import type { BrowserAuth, BrowserSession } from '@wtfalch/auth/browser';
import { type Agora, agoraOrigin } from './agoras.ts';

export type DesktopBridge = {
  session(): Promise<BrowserSession | null>;
  signIn(): Promise<BrowserSession | null>;
  signOut(): Promise<void>;
  /** Sets the dock icon's badge count -- the desktop shell's own side of
   *  the counts bridge (agora#219). Optional: a browser tab, or an older
   *  desktop build, has no such method at all, and `counts.ts`'s
   *  `reportBadgeCount` calls it only when it is actually present. */
  setBadgeCount?(count: number): Promise<void>;
  /** The running shell's own version (`app.getVersion()`), for the
   *  minimum-desktop-version gate (`desktop-version.ts`, agora#37).
   *  Optional the same way `setBadgeCount` is: a desktop build from before
   *  this existed has no such method, which the gate reads the same way it
   *  reads a version below the minimum -- too old to let in. */
  version?(): Promise<string>;
  /** Asks the shell to check its update feed right now -- the incompatible-
   *  version screen's own "Check for update" button. Rejects when this
   *  build has no update feed configured (an unpackaged dev build with no
   *  `AGORA_UPDATE_FEED_URL`). */
  checkForUpdates?(): Promise<void>;
  /** The companies this install holds -- origin and name only, never
   *  another company's own email (agora#430 review). Optional the same way
   *  `setBadgeCount` is: a desktop build from before this existed has no
   *  such method, and the org pane shows no company switcher there. */
  listCompanies?(): Promise<Array<{ origin: string; name?: string }>>;
  /** Resolves `email`'s own Agora the same way the welcome screen's email
   *  field does, confirms it with the person through a native prompt the
   *  page cannot spoof or suppress, and adds it to the stored list without
   *  switching to it. Null when nothing resolved, or the prompt was
   *  declined -- never a guess. */
  addCompany?(email: string): Promise<{ origin: string; name: string } | null>;
  /** Makes an already-added company's origin current and loads it -- never
   *  a second window, though a new one replaces the old one at the same
   *  size and position. Rejects on an origin this install never added, or
   *  one whose host does not answer -- the window stays on the company it
   *  was already on either way. */
  switchCompany?(origin: string): Promise<void>;
};
declare global {
  interface Window {
    wtfalchDesktop?: DesktopBridge;
  }
}
export const isDesktop = () => typeof window !== 'undefined' && !!window.wtfalchDesktop;

/** The bridge can mint a token only for the loaded origin, never another saved agora. */
export function desktopAuth(agora: Agora): BrowserAuth | undefined {
  const bridge = window.wtfalchDesktop;
  if (!bridge) return undefined;
  let current: BrowserSession | null = null;
  const check = () => {
    if (agoraOrigin(agora) !== window.location.origin)
      throw new Error('Open this company in its own desktop window.');
  };
  return {
    accountChanged: () => false,
    currentSession: () => current,
    silentSignInAvailable: () => false,
    trySilentSignIn: async () => false,
    completeSignIn: async () => ({
      kind: 'error',
      error: 'Complete desktop sign-in in your browser.',
    }),
    async signIn() {
      check();
      current = await bridge.signIn();
    },
    async refresh() {
      check();
      current = await bridge.session();
      return current ? { kind: 'refreshed', session: current } : { kind: 'no-refresh-token' };
    },
    async forget() {
      check();
      current = null;
      await bridge.signOut();
    },
  };
}
